// Type-only imports: no supabase client code runs in the shared package, so
// these are erased at build time and never pull @supabase's realtime/WebSocket
// client into the mobile bundle. (Both packages are devDependencies.)
import type { SupabaseClient } from '@supabase/supabase-js'
import type { FileObject } from "@supabase/storage-js";
import type { Database, Json } from './generated/public'
import type { RecordsThatHaveTodos, TodoAction } from '../lib/todos/types';

export type AppSupabaseClient = SupabaseClient<Database,'public', Database['public']>

export type Tables = Database['public']['Tables']
export type AnyTable = keyof Tables
export type Data<tableName extends keyof Tables> = Tables[tableName]['Row']

export type ProfileRow = Tables['profiles']['Row']
export type ProfileInsert = Tables['profiles']['Insert']
export type ProfileUpdate = Tables['profiles']['Update']

export type ExpensePaymentHistory = {
  _version: '2024-12-24',
  payments: Array<{
    date: string
    amount: string
    // Optional provenance, written when a payment is auto-recorded from a matched
    // Plaid transaction. Ignored by existing sum/display logic, which reads only
    // date + amount.
    source?: 'plaid'
    plaid_transaction_id?: string
    created_at?: string
    // Denormalized source-bank fields for plaid-sourced charges. Plaid does not
    // expose the card network, so the charge line is tagged with the originating
    // institution instead. Written by the Laravel side (PlaidPaymentLinker /
    // draft-creation job); older entries may lack these — treat as optional.
    institution_name?: string | null
    // Plaid's stable institution id. Not currently rendered by BankChargeChip
    // (institution_name is the display value) — carried through for future use
    // (e.g. per-institution icons/links) and because bankMetadata() writes all
    // three fields as one atomic unit; not speculative bloat.
    institution_id?: string | null
    account_mask?: string | null
  }>
}

export type ProviderExtraData = {
  billing_phone?: string | null
  billing_email?: string | null
  street_address?: string | null
  website?: string | null
}

/**
 * Tri-state shareability marking on an expense.
 *  - 'shareable'     eligible for the sharing ministry (DB column default)
 *  - 'not_shareable' not eligible (maintenance/OTC meds, Silver/Bronze, etc.)
 *  - 'unknown'       needs the member to decide (default for pharmacy-coded drafts)
 * The DB stores this as a plain text column; we narrow it here for type safety.
 */
export type Shareability = 'shareable' | 'not_shareable' | 'unknown'

/**
 * Provenance written by Alice's weekly auto-categorization pass when she
 * assigns a Plaid draft expense to an incident (or flags it as part of a
 * recurring series). See docs/design/draft-expense-auto-categorization.md.
 */
export type ExpenseAutoCategorization = {
  method: 'deterministic' | 'ai'
  confidence?: 'exact' | 'high' | 'medium'
  reasoning?: string
  // Present only on drafts flagged as part of a recurring series (payment
  // plan / subscription-like); these are NOT individually assigned.
  recurring_series?: {
    expense_ids: string[]
    cadence_days: number | null
    merchant: string
    reasoning: string
  }
}

export type ExpenseRow = Tables['expenses']['Row'] & {
  payment_history?: ExpensePaymentHistory | null
  provider_extra_data?: ProviderExtraData | null
  is_marked_not_duplicate?: boolean | null
  itemized_bill_conversation_id?: string | null
  shareability?: Shareability
  auto_categorized_at?: string | null
  auto_categorization?: ExpenseAutoCategorization | null
}

export const expenseGeneratedColumns = [
  'paidAmountNumeric',
  'listedAmountNumeric',
  'discountAmountNumeric'
] as const
export type ExpenseGeneratedColumns = typeof expenseGeneratedColumns[number]

export type ExpenseInsert = Omit<Tables['expenses']['Insert'], ExpenseGeneratedColumns> & {
  payment_history?: ExpensePaymentHistory | null
  provider_extra_data?: ProviderExtraData | null
  is_marked_not_duplicate?: boolean | null
  shareability?: Shareability
  auto_categorized_at?: string | null
  auto_categorization?: ExpenseAutoCategorization | null
}
export type ExpenseUpdate = Omit<Tables['expenses']['Update'], ExpenseGeneratedColumns> & {
  payment_history?: ExpensePaymentHistory | null
  provider_extra_data?: ProviderExtraData | null
  is_marked_not_duplicate?: boolean | null
  shareability?: Shareability
  auto_categorized_at?: string | null
  auto_categorization?: ExpenseAutoCategorization | null
}

export type IncidentRow = Tables['incidents']['Row']
export type IncidentInsert = Tables['incidents']['Insert']
export type IncidentUpdate = Tables['incidents']['Update']

export type MembershipRow = Tables['memberships']['Row']
export type MembershipUpdate = Tables['memberships']['Update']

export type MembershipConsentEventRow = Tables['membership_consent_events']['Row']

export type SharingMinistryType = 'CHM' | 'Sedera' | 'SMI' | null

/**
 * Samaritan Ministries (SMI) plan level, stored on memberships.smi_plan.
 * Determines the per-need Initial Unshareable Amount and sharing percentage —
 * see SMI_PLANS in lib/rules/limits.ts.
 */
export type SmiPlan = 'classic' | 'basic'

export type DependentRelationship = 'spouse' | 'child'

export type DependentRow = Omit<Tables['dependents']['Row'], 'relationship'> & {
  relationship?: DependentRelationship | null
}
export type DependentInsert = Omit<Tables['dependents']['Insert'], 'relationship'> & {
  relationship?: DependentRelationship | null
}

export type MembershipRoleType = 'admin' | 'user'
export type MembershipRoleRow = Tables['membership_roles']['Row'] & {
  role: MembershipRoleType
}

export type OrganizationRow = Tables['organizations']['Row']

export type OrganizationMembershipRow = Tables['organization_memberships']['Row']
export type OrganizationMembershipInsert = Tables['organization_memberships']['Insert']

export type PromoCodeRedemptionRow = Tables['promo_code_redemptions']['Row']

export type SettingsRow = Tables['settings']['Row']
export type SettingsUpdate = Tables['settings']['Update']

export type SubscriptionRow = Tables['subscriptions']['Row']

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'unpaid'

/**
 * The parsed, display-shaped OCR result stored in blobs.extracted_expense.
 * Derived server-side (Blob::deriveExtractedExpense in the admin panel) from
 * whichever raw OCR shape a producer wrote to textract_job_results, so the
 * client never parses that JSON itself. See docs/plans/native-app-sdk-seams.md,
 * Cleanup D.
 */
export type TextractExpenseData = {
  date?: string
  provider?: string
  listedAmount?: string
  discountAmount?: string
  paidAmount?: string
  summary?: string
}

export type BlobRow = Tables['blobs']['Row'] & {
  is_receipt_library_eligible?: boolean,
  blob_original_purpose?: AttachmentPurpose | null,
  extracted_expense?: TextractExpenseData | null
}
export type BlobInsert = Tables['blobs']['Insert'] & {
  is_receipt_library_eligible?: boolean,
  blob_original_purpose?: AttachmentPurpose | null,
  extracted_expense?: TextractExpenseData | null
}

export type AttachableTable = 'expenses' | 'incidents' | 'submissions' | 'advances' | 'advance_requests' | 'messages'
export type AttachmentPurpose = 'receipt' | 'itemized-bill' | 'sharing-request-packet' | 'add-on-worksheet' | 'email-attachment' | 'eob' | 'estimate'
export type AttachmentRow = Tables['attachments']['Row'] & {
  table_name: AttachableTable
  purpose?: AttachmentPurpose | null
} 
export type AttachmentInsert = Tables['attachments']['Insert'] & {
  table_name: AttachableTable
  purpose?: AttachmentPurpose | null
}
export type AttachmentUpdate = Tables['attachments']['Update'] & {
  table_name?: AttachableTable
  purpose?: AttachmentPurpose | null
}

export type ProviderDataSource =
  'textract' | 'google_places' | 'manual' | 'alice-phone-call' | 'bedrock_website_scan' | 'bedrock_receipt_scan'

export type ProviderRow = Tables['providers']['Row'] & {
  street_address_source?: ProviderDataSource | null
  billing_phone_source?: ProviderDataSource | null
  billing_email_source?: ProviderDataSource | null
  website_source?: ProviderDataSource | null
  communication_authorized_at?: string | null
  auto_request_itemized_bills?: boolean
}
export type ProviderInsert = Tables['providers']['Insert'] & {
  street_address_source?: ProviderDataSource | null
  billing_phone_source?: ProviderDataSource | null
  billing_email_source?: ProviderDataSource | null
  website_source?: ProviderDataSource | null
  communication_authorized_at?: string | null
  auto_request_itemized_bills?: boolean
}
export type ProviderUpdate = Tables['providers']['Update'] & {
  street_address_source?: ProviderDataSource | null
  billing_phone_source?: ProviderDataSource | null
  billing_email_source?: ProviderDataSource | null
  website_source?: ProviderDataSource | null
  communication_authorized_at?: string | null
  auto_request_itemized_bills?: boolean
}

export type SubmissionRow = Omit<Tables['submissions']['Row'], 'data' | 'submission_type'> & {
  data: unknown
  submission_type: SubmissionType
}
export type SubmissionInsert = Omit<Tables['submissions']['Insert'], 'data' | 'submission_type'> & {
  data?: unknown
  submission_type?: SubmissionType
}
export type SubmissionUpdate = Omit<Tables['submissions']['Update'], 'data' | 'submission_type'> & {
  data?: unknown
  submission_type?: SubmissionType
}

export const advanceGeneratedColumns = [
  'amountNumeric',
] as const
export type AdvanceGeneratedColumns = typeof advanceGeneratedColumns[number]

export type AdvanceRow = Tables['advances']['Row']
export type AdvanceInsert = Omit<Tables['advances']['Insert'], AdvanceGeneratedColumns>
export type AdvanceUpdate = Omit<Tables['advances']['Update'], AdvanceGeneratedColumns>

export const advanceRequestGeneratedColumns = [
  'amountNumeric',
  'approved_amount_numeric'
] as const
export type AdvanceRequestGeneratedColumns = typeof advanceRequestGeneratedColumns[number]

export type AdvanceRequestRow = Tables['advance_requests']['Row']
export type AdvanceRequestInsert = Omit<Tables['advance_requests']['Insert'], AdvanceRequestGeneratedColumns>
export type AdvanceRequestUpdate = Omit<Tables['advance_requests']['Update'], AdvanceRequestGeneratedColumns>

export type TodoRow = Omit<Tables['todos']['Row'], 'action'> & {
  record_type?: RecordsThatHaveTodos | null
  action?: TodoAction | null
}
export type TodoInsert = Omit<Tables['todos']['Insert'], 'action'> & {
  record_type?: RecordsThatHaveTodos | null
  action?: TodoAction | null
}
export type TodoUpdate = Omit<Tables['todos']['Update'], 'action'> & {
  record_type?: RecordsThatHaveTodos | null
  action?: TodoAction | null
}

export type MessageDirection = 'INBOUND' | 'OUTBOUND'
export type MessageChannel = 'email' | 'in-app'
export type ConversationTopicType = 'itemized-bill' | 'alice-chat' | 'alice-provider-chat'

export type AliceTaskStatus = 'pending' | 'in_progress' | 'completed'

export type AliceTaskItem = {
  task: string
  status: AliceTaskStatus
}

export type ConversationRow = Omit<Tables['conversations']['Row'], 'provider_id' | 'topic_type' | 'alice_goal' | 'alice_task_list'> & {
  provider_id: string | null
  topic_type: ConversationTopicType
  last_message_direction?: MessageDirection | null
  alice_goal?: string | null
  alice_task_list?: AliceTaskItem[] | null
}

export type ConversationInsert = Omit<Tables['conversations']['Insert'], 'provider_id' | 'topic_type' | 'alice_goal' | 'alice_task_list'> & {
  provider_id: string | null
  topic_type: ConversationTopicType
  last_message_direction?: MessageDirection | null
  alice_goal?: string | null
  alice_task_list?: AliceTaskItem[] | null
}

export type ConversationUpdate = Omit<Tables['conversations']['Update'], 'provider_id' | 'topic_type' | 'alice_goal' | 'alice_task_list'> & {
  provider_id?: string | null
  topic_type?: ConversationTopicType
  last_message_direction?: MessageDirection | null
  alice_goal?: string | null
  alice_task_list?: AliceTaskItem[] | null
}

export type MessageRow = Tables['messages']['Row'] & {
  direction?: MessageDirection | null
  channel?: MessageChannel
}

export type MessageInsert = Tables['messages']['Insert'] & {
  direction?: MessageDirection | null
  channel?: MessageChannel
}

export type MessageUpdate = Tables['messages']['Update'] & {
  direction?: MessageDirection | null
  channel?: MessageChannel
}

export type PhoneCallRow = Tables['phone_calls']['Row']
export type PhoneCallInsert = Tables['phone_calls']['Insert']
export type PhoneCallUpdate = Tables['phone_calls']['Update']

export type ProviderPhoneNumberRow = Tables['provider_phone_numbers']['Row']

/**
 * The set of all records that can be the referent of a notification.
 */
export const NotificationRecordTypes = [
  'advance_request',
  'advance',
  'todo',
  'conversation',
  'expense'
] as const
export type NotificationRecordType = typeof NotificationRecordTypes[number]

export function raiseUnknownNotificationRecordType(type: never): never {
  throw new Error(`Unknown notification record type ${type}`)
}

// Maps the "record_type" field of a Notification to the Table that can be queried to find that record.
const notificationRecordTypeTableMap = {
  'advance_request': 'advance_requests',
  'advance': 'advances',
  'todo': 'todos',
  'conversation': 'conversations',
  'expense': 'expenses'
} as const

export type NotificationRecordTypeTableMap = typeof notificationRecordTypeTableMap

export type NotificationRecordTypeTable = NotificationRecordTypeTableMap[keyof NotificationRecordTypeTableMap]

export function getNotificationRecordTypeTable(recordType: NotificationRecordType): NotificationRecordTypeTable {
  return notificationRecordTypeTableMap[recordType]
}

// Maps the "record_type" field of a Notification to the Row type of that record.
export type NotificationRecordTypeRowMap = {
  'advance_request': AdvanceRequestRow,
  'advance': AdvanceRow,
  'todo': TodoRow,
  'conversation': ConversationRow,
  'expense': ExpenseRow
}

export type NotificationRecordTypeRow<T extends NotificationRecordType = NotificationRecordType> =
  NotificationRecordTypeRowMap[T];

/**
 * The set of all notification types.
 */
export const NotificationTypes = [
  'advance_request_approved',
  'advance_request_rejected',
  'advance_created',
  'todo_reminder',
  'provider_reply_received',
  'itemized_bill_matched',
  'alice_escalation',
  'alice_provider_update',
  'ai_call_completed',
  'ai_call_screening',
  'expense_auto_categorized',
] as const
export type NotificationType = typeof NotificationTypes[number]

export function raiseUnknownNotificationType(type: never): never {
  throw new Error(`Unknown notification type ${type}`)
}

export type NotificationRow = Tables['notifications']['Row'] & {
  notification_type: NotificationType
  record_type?: NotificationRecordType | null
}
export type NotificationInsert = Tables['notifications']['Insert'] & {
  notification_type: NotificationType
  record_type?: NotificationRecordType | null
}
export type NotificationUpdate = Tables['notifications']['Update'] & {
  notification_type: NotificationType
  record_type?: NotificationRecordType | null
}

/**
 * This is the data that is sent to the service worker when a push notification is received.
 */
export type PushNotificationData = {
  /**
   * The Firebase push token that we used to send the push notification.
   */
  token: string
  
  /**
   * The notification row UUID in the Supabase database.
   */
  id: string
  
  notification_type: NotificationType
  record_type?: NotificationRecordType
  record_id?: string
}

export type SubmissionType =
  'CHM' |
  'CHM-addon' |
  'CHM-personal-responsibility' |
  'Sedera' |
  'Sedera-addon' |
  'SMI' |
  'SMI-addon' |
  'HRA'

export function raiseUnknownSubmissionType(type: never): never {
  throw new Error(`Unknown submission type ${type}`)
}

export type StorageItem = FileObject | DirObject

export interface DirObject {
  name: string
  id: null
  updated_at: null
  created_at: null
  last_accessed_at: null
  metadata: null
}

export function isDir(file: FileObject | DirObject): file is DirObject {
  return !file.id
}

export function isFile(file: FileObject | DirObject): file is FileObject {
  return !!file.id
}

export type Deleted<T> = T & { deleted_at: string }
export function isDeleted<T extends Record<string, any>>(record: T): record is Deleted<T> {
  return 'deleted_at' in record && !!record.deleted_at
}

export function assertDeleted<T extends { deleted_at?: string | null }>(record: T): asserts record is Deleted<T> {
  if (!isDeleted(record)) {
    throw new Error(`Record ${record} is not deleted`)
  }
}

export type NotDeleted<T extends { deleted_at?: string | null }> = Omit<T, 'deleted_at'>
export function isNotDeleted<T extends { deleted_at?: string | null }>(record: T): record is T & NotDeleted<T> {
  return !record.deleted_at
}
